import { combineReducers } from 'redux';
import { GET_ERRORS, LOGGED_OUT } from '../actions/types';
import navigationReducer from './navigationReducer';
import appReducer from './appReducer';
import authReducer from './asyncReducer';
import modalReducer from './modalReducer';
import errorReducer from './errorReducer';
import uiReducer from './uiReducer';
import tutorialReducer from './tutorialReducer';
import uploadPreviewReducer from './uploadPreviewReducer';
import achievementReducer from './achievementReducer';
import widgetReducer from './widgetReducer';
import toolbarReducer from './toolbarReducer';
import storeReducer from './storeReducer';
import calendarReducer from './calendarReducer';
import notificationReducer from './notificationReducer';
import userHierarchyReducer from './userManagementReducer';
import projectReducer from './projectReducer';
import reportReducer from './reportReducer';
import actionHandlerReducer from './actionHandlerReducer';
import agentBoardReducer from './agentBoardReducer';

const sanitizeErrorPayload = (payload: any) => {
	if (!payload) {
		return { message: 'Unknown error' };
	}
	if (typeof payload === 'string') {
		return { message: payload };
	}
	if (payload.response) {
		return sanitizeErrorPayload(payload.response);
	}
	const { status, statusText, data, headers, config } = payload;
	const safeConfig = config
		? {
				url: config.url,
				method: config.method,
				baseURL: config.baseURL,
				params: config.params,
			}
		: undefined;
	return {
		status,
		statusText,
		data,
		headers,
		config: safeConfig,
		message:
			(typeof data === 'object' &&
				data !== null &&
				'message' in data &&
				data.message) ||
			statusText ||
			'Unknown error',
	};
};

const sanitizeActionForLastAction = (action: any) => {
	if (!action || typeof action !== 'object') {
		return action;
	}
	const sanitized: any = { ...action };
	if ('error' in sanitized) {
		sanitized.error = sanitizeErrorPayload(sanitized.error);
	}
	if (
		sanitized.data &&
		typeof sanitized.data === 'object' &&
		'error' in sanitized.data
	) {
		sanitized.data = {
			...sanitized.data,
			error: sanitizeErrorPayload((sanitized.data as any).error),
		};
	}
	if (
		sanitized.payload &&
		typeof sanitized.payload === 'object' &&
		'error' in sanitized.payload
	) {
		sanitized.payload = {
			...sanitized.payload,
			error: sanitizeErrorPayload((sanitized.payload as any).error),
		};
	}
	return sanitized;
};

const rootReducers = (state, action) => {
	if (action.type === LOGGED_OUT) {
		state = undefined;
	}
	const processedAction =
		action?.type === GET_ERRORS
			? { ...action, payload: sanitizeErrorPayload(action.payload) }
			: action;
	return allReducers(state, processedAction);
};

function lastAction(state = null, action) {
	return sanitizeActionForLastAction(action);
}

const allReducers = combineReducers({
	promiseData: authReducer,
	app: appReducer,
	navigation: navigationReducer,
	modals: modalReducer,
	errors: errorReducer,
	UI: uiReducer,
	uploadPreview: uploadPreviewReducer,
	notifications: notificationReducer,
	tutorials: tutorialReducer,
	calendar: calendarReducer,
	widgets: widgetReducer,
	achievements: achievementReducer,
	toolbar: toolbarReducer,
	store: storeReducer,
	lastAction: lastAction,
	userHierarchy: userHierarchyReducer,
	project: projectReducer,
	report: reportReducer,
	actionMaker: actionHandlerReducer,
	agentBoard: agentBoardReducer
});

export default rootReducers;
